/**
 * WordPress dependencies
 */
import {
	BaseControl,
	RangeControl,
	parseQuantityAndUnitFromRawValue,
	useCustomUnits,
} from '@safe-wordpress/components';
import { useState } from '@safe-wordpress/element';
import { _x } from '@safe-wordpress/i18n';

/**
 * Internal dependencies
 */
import AllInputControl from './all-input-control';
import InputControls from './input-controls';
import LinkedButton from './linked-button';
import {
	getAllValue,
	getAllUnit,
	hasDefinedValues,
	hasMixedValues,
} from './utils';

const DEFAULT_VALUES = {
	topLeft: undefined,
	topRight: undefined,
	bottomLeft: undefined,
	bottomRight: undefined,
};
const MIN_BORDER_RADIUS_VALUE = 0;
const MAX_BORDER_RADIUS_VALUES: Record< string, number > = {
	px: 100,
	em: 20,
	rem: 20,
};

/**
 * Control to display border radius options.
 *
 * @param {Object}   props          Component props.
 * @param {Function} props.onChange Callback to handle onChange.
 * @param {Object}   props.values   Border radius values.
 *
 * @return {Element}              Custom border radius control.
 */
export type BorderRadiusControlProps = {
	readonly values?:
		| string
		| {
				readonly flat?: string;
				readonly topLeft?: string;
				readonly topRight?: string;
				readonly bottomLeft?: string;
				readonly bottomRight?: string;
		  };
	readonly onChange: (
		value?:
			| string
			| {
					readonly flat?: string;
					readonly topLeft?: string;
					readonly topRight?: string;
					readonly bottomLeft?: string;
					readonly bottomRight?: string;
			  }
	) => void;
	readonly readonlyCorners: {
		readonly topLeft: boolean;
		readonly topRight: boolean;
		readonly bottomLeft: boolean;
		readonly bottomRight: boolean;
	};
};
export function BorderRadiusControl( {
	onChange,
	values,
	readonlyCorners,
}: BorderRadiusControlProps ): JSX.Element {
	const [ isLinked, setIsLinked ] = useState(
		! hasDefinedValues( values ) || ! hasMixedValues( values )
	);

	// Tracking selected units via internal state allows filtering of CSS unit
	// only values from being saved while maintaining preexisting unit selection
	// behaviour. Filtering CSS unit only values prevents invalid style values.
	const [ selectedUnits, setSelectedUnits ] = useState< {
		readonly flat?: string;
		readonly topLeft?: string;
		readonly topRight?: string;
		readonly bottomLeft?: string;
		readonly bottomRight?: string;
	} >( {
		flat:
			typeof values === 'string'
				? parseQuantityAndUnitFromRawValue( values )[ 1 ]
				: undefined,
		topLeft:
			typeof values === 'string'
				? parseQuantityAndUnitFromRawValue( values )[ 1 ]
				: parseQuantityAndUnitFromRawValue( values?.topLeft )[ 1 ],
		topRight:
			typeof values === 'string'
				? parseQuantityAndUnitFromRawValue( values )[ 1 ]
				: parseQuantityAndUnitFromRawValue( values?.topRight )[ 1 ],
		bottomLeft:
			typeof values === 'string'
				? parseQuantityAndUnitFromRawValue( values )[ 1 ]
				: parseQuantityAndUnitFromRawValue( values?.bottomLeft )[ 1 ],
		bottomRight:
			typeof values === 'string'
				? parseQuantityAndUnitFromRawValue( values )[ 1 ]
				: parseQuantityAndUnitFromRawValue( values?.bottomRight )[ 1 ],
	} );

	const units = useCustomUnits( {
		availableUnits: [ 'px', 'em', 'rem' ],
	} );

	const unit = getAllUnit( selectedUnits );
	const unitConfig = units && units.find( ( item ) => item.value === unit );
	const step = unitConfig?.step || 1;

	const isCompletelyReadonly = Object.values( readonlyCorners ).every(
		( c ) => c
	);
	const isCompletelyEditable = Object.values( readonlyCorners ).every(
		( c ) => ! c
	);

	const [ allValue ] = parseQuantityAndUnitFromRawValue(
		getAllValue( values )
	);

	const toggleLinked = () => setIsLinked( ! isLinked );

	const handleSliderChange = ( next?: number ) => {
		onChange( next !== undefined ? `${ next }${ unit }` : undefined );
	};

	return (
		<fieldset className="components-border-radius-control">
			<BaseControl.VisualLabel as="legend">
				{ _x( 'Radius', 'text', 'nelio-ab-testing' ) }
			</BaseControl.VisualLabel>
			<div className="components-border-radius-control__wrapper">
				{ isLinked &&
				( isCompletelyReadonly || isCompletelyEditable ) ? (
					<>
						<AllInputControl
							className="components-border-radius-control__unit-control"
							values={ values }
							min={ MIN_BORDER_RADIUS_VALUE }
							onChange={ onChange }
							selectedUnits={ selectedUnits }
							setSelectedUnits={ setSelectedUnits }
							units={ units }
							disabled={ isCompletelyReadonly }
						/>
						<RangeControl
							__next40pxDefaultSize
							label={ _x(
								'Border radius',
								'text',
								'nelio-ab-testing'
							) }
							hideLabelFromVision
							className="components-border-radius-control__range-control"
							value={ allValue }
							min={ MIN_BORDER_RADIUS_VALUE }
							max={ MAX_BORDER_RADIUS_VALUES[ unit ] }
							initialPosition={ 0 }
							withInputField={ false }
							onChange={ handleSliderChange }
							step={ step }
							disabled={ isCompletelyReadonly }
							__nextHasNoMarginBottom
						/>
					</>
				) : (
					<InputControls
						min={ MIN_BORDER_RADIUS_VALUE }
						onChange={ onChange }
						selectedUnits={ selectedUnits }
						setSelectedUnits={ setSelectedUnits }
						values={ values || DEFAULT_VALUES }
						units={ units }
						readonlyCorners={ readonlyCorners }
					/>
				) }
				{ isCompletelyEditable && (
					<LinkedButton
						onClick={ toggleLinked }
						isLinked={ isLinked }
					/>
				) }
			</div>
		</fieldset>
	);
}
